import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, message, Modal, Descriptions, Tag, Empty } from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import DeviceCard from '../DeviceCard';
import CommonPagination from '../CommonPagination';
import s from './index.module.css';
import { getPublicDeviceList, getCategoryChildren } from '../../server/api';

// 行业分类来源：系统管理-分类字典-一站式服务类别-仪器设备服务（B03A07）
const INDUSTRY_PCODE = 'B03A07';

const MANAGE_TYPE_TEXT = {
  1: '检定',
  2: '校准',
  3: '期间核查',
};

const STATUS_TEXT = {
  1: '正常使用',
  2: '待用/检修中',
  3: '停用',
};

const getRecords = (result) => {
  if (Array.isArray(result?.records)) return result.records;
  if (Array.isArray(result?.list)) return result.list;
  if (Array.isArray(result)) return result;
  return [];
};

const getTotal = (result) => {
  const total = Number(result?.total);
  if (!Number.isNaN(total) && total >= 0) return total;
  return getRecords(result).length;
};

const formatDay = (value) => {
  if (!value) return '-';
  return String(value).slice(0, 10);
};

// 分类树节点 → { id, name }
const normalizeCategories = (list) =>
  (Array.isArray(list) ? list : [])
    .filter((item) => item && item.key)
    .map((item) => ({ id: item.key, name: item.title || item.value || '' }));

const EquipmentSharingList = ({ showHeader = true, embedded = false }) => {
  const history = useHistory();
  const location = useLocation();

  const query = new URLSearchParams(location.search || '');
  const [categories, setCategories] = useState([]);
  const [activeType, setActiveType] = useState(() => {
    const type = query.get('industryType');
    return type ? type : 'all';
  });
  const [devices, setDevices] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  // 加载行业分类（B03A07 仪器设备服务子分类）
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await getCategoryChildren({ pcode: INDUSTRY_PCODE });
        setCategories(normalizeCategories(res.data?.result));
      } catch (_) {
        setCategories([]);
      }
    };
    loadCategories();
  }, []);

  const fetchDevices = useCallback(async (type, page, size) => {
    setLoading(true);
    try {
      const params = { pageNo: page, pageSize: size };
      if (type !== 'all') params.industryType = type;
      const res = await getPublicDeviceList(params);
      const result = res.data?.result;
      setDevices(getRecords(result));
      setTotal(getTotal(result));
    } catch (_) {
      message.error('设备数据加载失败，请刷新重试！');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices(activeType, pageNo, pageSize);
  }, [activeType, pageNo, pageSize, fetchDevices]);

  // URL 携带设备 id 时展示详情
  useEffect(() => {
    const viewId = query.get('view');
    if (viewId) {
      const device = devices.find((item) => item.id === viewId);
      if (device) setDetail(device);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, devices]);

  const buildUrl = (type) => {
    if (type === 'all') return '/equipment-sharing';
    return `/equipment-sharing?industryType=${encodeURIComponent(type)}`;
  };

  const handleTypeChange = (type) => {
    setActiveType(type);
    setPageNo(1);
    if (!embedded) history.replace(buildUrl(type));
  };

  const handlePageChange = (page, size) => {
    setPageNo(page);
    if (size) setPageSize(size);
  };

  const handleDeviceClick = (device) => {
    setDetail(device);
    if (!embedded) {
      const sep = activeType === 'all' ? '?' : '&';
      history.replace(`${buildUrl(activeType)}${sep}view=${device.id}`);
    }
  };

  const handleCloseDetail = () => {
    setDetail(null);
    if (!embedded) history.replace(buildUrl(activeType));
  };

  const typeTabs = [{ key: 'all', label: '全部设备' }].concat(
    categories.map((item) => ({ key: item.id, label: item.name }))
  );

  const categoryMap = categories.reduce((acc, item) => {
    acc[item.id] = item.name;
    return acc;
  }, {});

  const detailDevice = detail || {};
  const detailManageText = MANAGE_TYPE_TEXT[detailDevice.manageType] || '-';
  const detailStatusText = STATUS_TEXT[detailDevice.deviceStatus] || '-';
  const detailIndustryText = categoryMap[detailDevice.industryType] || detailDevice.industryType_dictText || '-';

  return (
    <div className={embedded ? `${s.outter} ${s.centered}` : s.outter}>
      <div className={s.container}>
      {showHeader && (
        <div className={s.listHeader}>
          <div className={s.headerIcon}><ExperimentOutlined /></div>
          <div>
            <h2 className={s.listTitle}>仪器设备服务</h2>
            <p className={s.listSubtitle}>展示全市检测机构在用仪器设备的行业分类、检定校准及共享服务信息</p>
          </div>
        </div>
      )}

      <div className={s.typePanel}>
        <div className={s.typeTabs}>
          {typeTabs.map((item) => (
            <div
              key={item.key}
              className={`${s.typeTab} ${activeType === item.key ? s.typeTabActive : ''}`}
              onClick={() => handleTypeChange(item.key)}
            >
              {item.label}
            </div>
          ))}
        </div>
      </div>

      <div className={s.devicePanel}>
        <div className={s.devicePanelHeader}>
          <span className={s.devicePanelTitle}>设备列表</span>
          <span className={s.deviceCount}>
            共 <strong>{total}</strong> 台设备
          </span>
        </div>

        {devices.length > 0 ? (
          <>
            <Row gutter={[16, 16]} className={s.deviceGrid}>
              {devices.map((device) => (
                <Col xs={24} sm={12} md={8} lg={6} xl={6} key={device.id}>
                  <DeviceCard device={device} onClick={handleDeviceClick} />
                </Col>
              ))}
            </Row>

            <div className={s.paginationWrap}>
              <CommonPagination
                current={pageNo}
                pageSize={pageSize}
                total={total}
                onChange={handlePageChange}
                onShowSizeChange={handlePageChange}
              />
            </div>
          </>
        ) : (
          <div className={s.listEmpty}>
            <Empty description="该分类下暂无设备" />
          </div>
        )}
      </div>

      <Modal
        title={detailDevice.deviceName || '设备详情'}
        open={!!detail}
        onCancel={handleCloseDetail}
        footer={null}
        width={640}
        className={s.detailModal}
      >
        {detail && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="设备名称">{detailDevice.deviceName || '-'}</Descriptions.Item>
            <Descriptions.Item label="规格型号">{detailDevice.deviceModel || '-'}</Descriptions.Item>
            <Descriptions.Item label="设备编号">{detailDevice.deviceNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="生产厂家">{detailDevice.manufacturer || '-'}</Descriptions.Item>
            <Descriptions.Item label="所属机构">{detailDevice.orgName || '-'}</Descriptions.Item>
            <Descriptions.Item label="联系电话">
              {(detailDevice.orgTel || detailDevice.contactTel) ? (
                <a href={`tel:${detailDevice.orgTel || detailDevice.contactTel}`}>
                  {[detailDevice.orgContact && `联系人：${detailDevice.orgContact}`, `电话：${detailDevice.orgTel || detailDevice.contactTel}`].filter(Boolean).join('  ')}
                </a>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="所属实验室">{detailDevice.labName || '-'}</Descriptions.Item>
            <Descriptions.Item label="行业分类">
              {detailIndustryText !== '-' ? <Tag color="blue">{detailIndustryText}</Tag> : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="管理方式">
              {detailManageText !== '-' && <Tag color="cyan">{detailManageText}</Tag>}
              {detailManageText === '-' && '-'}
            </Descriptions.Item>
            <Descriptions.Item label="使用状态">
              <Tag color={detailDevice.deviceStatus === 1 ? 'green' : detailDevice.deviceStatus === 2 ? 'orange' : 'red'}>
                {detailStatusText}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="有效期至">{formatDay(detailDevice.validEndDate)}</Descriptions.Item>
            {detailDevice.remark && <Descriptions.Item label="备注">{detailDevice.remark}</Descriptions.Item>}
          </Descriptions>
        )}
      </Modal>
      </div>
    </div>
  );
};

export default EquipmentSharingList;